import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

// Generic fetch hook
function useCmsData<T>(table: string, options?: { orderBy?: string; filter?: Record<string, any>; enabled?: boolean }) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (options?.enabled === false) return;
    const fetch = async () => {
      let query = supabase.from(table as any).select("*");
      if (options?.filter) {
        Object.entries(options.filter).forEach(([key, value]) => {
          query = query.eq(key, value);
        });
      }
      if (options?.orderBy) {
        query = query.order(options.orderBy);
      }
      const { data: result } = await query;
      if (result) setData(result as any);
      setLoading(false);
    };
    fetch();
  }, [table, options?.enabled]);

  return { data, loading };
}

export interface CmsProduct {
  id: string;
  name: string;
  category: string;
  brand: string;
  short_name?: string;
  description?: string;
  long_description?: string;
  price?: string;
  image?: string;
  images: string[];
  features: string[];
  benefits: string[];
  applications: string[];
  specifications: { label: string; value: string }[];
  filtration_stages: { stage: number; name: string; description: string }[];
  certifications: string[];
  made_in: string;
  is_active: boolean;
  sort_order: number;
  seo_title?: string;
  seo_description?: string;
  is_promoted?: boolean;
  promotion_label?: string;
}

export const useCmsProducts = (filter?: Record<string, any>) =>
  useCmsData<CmsProduct>("cms_products", { orderBy: "sort_order", filter });

export const useCmsProduct = (id: string) => {
  const [product, setProduct] = useState<CmsProduct | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase.from("cms_products" as any).select("*").eq("id", id).maybeSingle();
      if (data) setProduct(data as any);
      setLoading(false);
    };
    fetch();
  }, [id]);

  return { product, loading };
};

export interface CmsVideo {
  id: string;
  title: string;
  url: string;
  description?: string;
  section: string;
  product_id?: string;
  is_active: boolean;
  sort_order: number;
}

export const useCmsVideos = (section?: string) => {
  const result = useCmsData<CmsVideo>("cms_videos", { orderBy: "sort_order" });
  const filtered = section
    ? { ...result, data: result.data.filter((v) => v.section.split(",").includes(section)) }
    : result;
  return filtered;
};

export interface CmsCertification {
  id: string;
  name: string;
  full_name?: string;
  description?: string;
  image_url?: string;
  badge_color: string;
  is_active: boolean;
  sort_order: number;
  brand: string;
}

export const useCmsCertifications = () =>
  useCmsData<CmsCertification>("cms_certifications", { orderBy: "sort_order" });

export interface CmsBranch {
  id: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  phone?: string;
  email?: string;
  is_active: boolean;
  sort_order: number;
}

export const useCmsBranches = () =>
  useCmsData<CmsBranch>("cms_branches", { orderBy: "sort_order" });

export interface CmsDocument {
  id: string;
  title: string;
  type: string;
  description?: string;
  file_url?: string;
  product_id?: string;
  thumbnail_image?: string;
  thumbnail_alt?: string;
  is_active: boolean;
  sort_order: number;
}

export const useCmsDocuments = (type?: string) => {
  const filter = type ? { type } : undefined;
  return useCmsData<CmsDocument>("cms_documents", { orderBy: "sort_order", filter });
};

export interface CmsHomepageSection {
  id: string;
  section_name: string;
  content: any;
  is_enabled: boolean;
  sort_order: number;
}

export const useCmsHomepageSections = () =>
  useCmsData<CmsHomepageSection>("cms_homepage_sections", { orderBy: "sort_order" });

export const useCmsHomepageSection = (id: string) => {
  const [section, setSection] = useState<CmsHomepageSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase.from("cms_homepage_sections" as any).select("*").eq("id", id).maybeSingle();
      if (data) setSection(data as any);
      setLoading(false);
    };
    fetch();
  }, [id]);

  return { section, loading };
};

export interface CmsAboutContent {
  id: string;
  section_key: string;
  content: any;
}

export const useCmsAboutContent = () =>
  useCmsData<CmsAboutContent>("cms_about_content");

// Helper to extract YouTube ID from URL
export const getYoutubeId = (url: string): string => {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return match?.[1] || "";
};

// Product categories for filter UI
export const cmsProductCategories = [
  { id: "all", label: "All Products" },
  { id: "ionizers", label: "Water Ionizers" },
  { id: "ro-systems", label: "RO Systems" },
  { id: "perfect-water", label: "Perfect Water" },
  { id: "hydrogen", label: "Hydrogen Products" },
  { id: "filters", label: "Filters" },
  { id: "ro-filters", label: "RO Filters & Spares" },
];
